import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";

type JsonApiDisbursementItem = {
    id: string;
    type: string;
    attributes: {
        id: number;
        source: "provider" | "loan" | "insurance";
        sourcePartyId: string | null;
        locked: boolean;
        company: string;
        accountNumber: string | null;
        type: string;
        bill: number;
        insurance: number;
        adj: number;
        self: number;
        medPay: number;
        currentBalance: number;
        reduction: number;
        sent: string | null;
        approved: string | null;
        sortOrder: number;
    };
};

type JsonApiDisbursementResponse = {
    data: JsonApiDisbursementItem[];
    meta?: {
        totalRowCount: number;
    };
};

export type DisbursementRow = {
    idUUID: string;
    id: number;
    source: "provider" | "loan" | "insurance";
    sourcePartyId: string | null;
    locked: boolean;
    company: string;
    accountNumber: string | null;
    type: string;
    bill: number;
    insurance: number;
    adj: number;
    self: number;
    medPay: number;
    currentBalance: number;
    reduction: number;
    sent: string | null;
    approved: string | null;
    sortOrder: number;
};

export type DisbursementRowsDocument = {
    data: DisbursementRow[];
    meta?: {
        totalRowCount: number;
    };
};

type Props = {
    caseId: string;
};

export const useFetchDisbursementRows = ({
    caseId,
}: Props): UseQueryResult<DisbursementRowsDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["disbursement", caseId],
        enabled: caseId !== undefined,
        queryFn: async () => {
            const response = await fetch(apiUrl(`/disbursement/case/${caseId}`), {
                method: "GET",
                headers: {
                    accept: "application/vnd.api+json",
                },
            });

            return await response.json();
        },
        select: (jsonApiResponse: JsonApiDisbursementResponse): DisbursementRowsDocument => ({
            data:
                jsonApiResponse.data?.map((item) => ({
                    idUUID: item.id,
                    ...item.attributes,
                })) || [],
            meta: jsonApiResponse.meta,
        }),
    });
};
